/**
 * What a report is made of.
 *
 * A report is a tree of sections. Most are leaves that render something; one
 * kind, `group`, holds other sections and is what gives a report its chapters.
 * Everything else in the builder, the outline, the canvas, the exporters, is a
 * different way of looking at this one structure.
 *
 * Two decisions shape the whole model.
 *
 * **Every section carries its kind as a literal**, so a discriminated union
 * narrows it. A renderer that handles `heading` receives something with a
 * `level`, and one that handles `table` receives something with `rows`,
 * without either casting. Adding a kind then makes every incomplete `switch`
 * a compile error rather than a blank space on a page.
 *
 * **Ids are stable and generated once.** They anchor comments, drag
 * operations, selection and the contents page. Deriving them from position
 * would move every comment in a report each time somebody reordered two
 * paragraphs.
 */

import { createSectionOfKind } from '@/sections'
import type { ChartContent, ChartSeries } from '@/domain/chart'
import type { TextColumns, TextSetting } from '@/domain/prose'
import type { SectionTag } from '@/domain/esrs'
import type { FacsimileFrame } from '@/domain/facsimile/types'
import type { FreeElement } from '@/domain/free'
import type { PageBackground } from '@/domain/layout/background'
import type { GhgLine } from '@/domain/ghg'
import type { TableLook } from '@/sections/table/look'
import type { TaxonomyKpi } from '@/domain/taxonomy'
import type { CoverVariant } from '@/sections/cover/variants'
import type { GroupAlign, GroupColumns } from '@/sections/group/layout'

/** The kinds of section a report can contain. */
export type SectionKind =
  | 'cover'
  | 'group'
  | 'heading'
  | 'text'
  | 'table'
  | 'image'
  | 'chart'
  | 'kpi'
  | 'note'
  | 'scope'
  | 'glossary'
  | 'contents'
  | 'signatures'
  | 'divider'
  | 'pageBreak'
  | 'imagePage'
  | 'fixedPage'
  | 'datapoint'
  | 'target'
  | 'materiality'
  | 'iro'
  | 'risks'
  | 'ghg'
  | 'taxonomy'
  | 'disclosureIndex'
  | 'layer'

/** What every section has, whatever its kind. */
interface SectionBase {
  readonly id: string
  /**
   * What this section is called in the outline.
   *
   * Distinct from anything it renders: a table's title appears above it, but a
   * page break still needs a name in the outline, and a group's name is its
   * chapter heading. Keeping them separate means the outline never shows a
   * blank row.
   */
  title: string
  /**
   * What this section discloses, under the ESRS.
   *
   * On the base rather than on a kind of its own, because what a section
   * discloses is orthogonal to what it is: a table, a chart, a note and a
   * whole chapter can each answer `E1-6`. A section kind called "ESRS block"
   * would only mean the tag was unavailable on every other kind.
   *
   * Absent on almost every section of almost every report, which is why it is
   * optional rather than a blank string: a financial statement has no ESRS
   * references in it at all, and should not carry an empty field per section
   * through every save.
   */
  tag?: SectionTag
  /**
   * The directions this section is given to: only people belonging to one of
   * them may change it (an administrator may change anything).
   *
   * Absent on almost every section, and that means open to whoever may edit the
   * report. A section with none of its own takes its chapter's (see
   * `domain/direction.ts`), which is why a chapter is given once, not each of
   * its sections.
   */
  directions?: readonly string[]
  /**
   * Set by the server on a section this person may not read: it arrives as a
   * shell (its place and its title, none of its content) and is drawn as a
   * black block. Never set by the page, and handed back untouched on a save,
   * where the server puts the real section in its place.
   */
  masked?: true
}

/** The front page: who the report is by, about, and for what period. */
export interface CoverSection extends SectionBase {
  readonly kind: 'cover'
  /**
   * Which arrangement the page uses.
   *
   * Named rather than a set of switches: "the one with the photograph" is a
   * decision somebody makes once, and offering the parts separately would let
   * them build arrangements nobody has ever looked at.
   */
  variant: CoverVariant
  entity: string
  period: string
  subtitle: string
  /** Shown under the title, for a standard or a basis of preparation. */
  standard: string
  /**
   * A photograph or illustration, as a data URL. Empty when there is none.
   *
   * Held in the document like every other image, so a report opened next year
   * does not depend on a file server somebody has since tidied up.
   */
  picture: string
  /** A mark or wordmark, as a data URL. Separate from the picture: a cover
   *  routinely carries both, and they are never interchangeable. */
  logo: string
  /** The line at the foot: a reference, a version, a confidentiality note. */
  note: string
}

/**
 * A chapter: a section that holds other sections.
 *
 * The one recursive kind, and the reason the document is a tree rather than a
 * list. Groups may nest, so a report can have parts, chapters and subsections
 * without any of those being separate concepts.
 */
export interface GroupSection extends SectionBase {
  readonly kind: 'group'
  children: Section[]
  /** Collapsed in the outline. A view concern, kept with the section so it
   *  survives a reload rather than resetting every time. */
  collapsed: boolean
  /** Whether the group's own title is printed as a heading. A group used
   *  purely to organise the outline often should not appear in the document. */
  showTitle: boolean
  /**
   * How many of its sections sit side by side.
   *
   * One by default, which is a chapter as it has always been. More turns the
   * chapter into a row: four key figures, two charts, three datapoints. What
   * it never changes is the order, which stays the document's: across, then
   * down, so the outline, the contents, the export and a screen reader all
   * still agree with the page.
   */
  columns: GroupColumns
  /** How sections of different heights line up across a row. */
  align: GroupAlign
}

/** A heading in the document. */
export interface HeadingSection extends SectionBase {
  readonly kind: 'heading'
  /** 1 is the largest. Bounded, because a document with nine levels of
   *  heading has a structural problem no renderer can fix. */
  level: 1 | 2 | 3 | 4
  /**
   * A named text style the heading is set in, or empty for the document's
   * own heading style. See `TextSection.textStyleId` for why the bare id is
   * kept. A heading imported from a PDF wears the style it was printed in.
   */
  textStyleId: string
  /**
   * What the contents page calls it, where that is not its title: a contents
   * line is often a description rather than the heading itself. Absent, the
   * title is used.
   */
  contentsLabel?: string
  /** Left out of the contents page. Only ever true or absent. */
  hideInContents?: true
  /**
   * The colour of a chapter (a heading of the first level), `#rrggbb`: its
   * line in the contents page is set in it. Absent, the next colour of the
   * document's palette, in the order the chapters come.
   */
  colour?: string
  /** A line under its entry in the contents page, in the author's words. */
  contentsDetail?: string
}

/** Prose. Holds HTML, produced by the editor or by an import. */
export interface TextSection extends SectionBase, TextSetting {
  readonly kind: 'text'
  html: string
  /**
   * How many columns the text flows across.
   *
   * A magazine-style split, not the side-by-side layout a group offers: one
   * body of text that runs from the foot of the first column to the head of
   * the second, because to whoever is drafting it, it is one piece of prose
   * and splitting it into two sections would break that. The rest of how the
   * block is set (gap, rule, alignment, leading, frame, drop cap) is its
   * `TextSetting`, see `prose.ts`.
   */
  columns: TextColumns
  /**
   * A named text style applied to the whole block, or empty for none.
   *
   * The id of a `TextStyle` from the document's own layout — see
   * `domain/layout/textStyles.ts`. Kept as a bare id rather than dropped once
   * validated, because the style it names can be edited or removed later: an
   * id that no longer matches anything simply stops colouring the block,
   * rather than the block losing its own memory of which one it meant.
   */
  textStyleId: string
}

/** Where the text of a cell sits, when somebody chose. */
export type CellAlign = 'left' | 'center' | 'right'

/**
 * One cell of a table.
 *
 * The first two flags say what the cell *is*; the last three say how somebody
 * chose to set it, and are absent until they do. Absent rather than defaulted,
 * so a cell nobody touched follows the document's table settings and keeps
 * following them when those change.
 */
export interface TableCell {
  value: string
  /** Right-align and use lining figures. Set on import when a column reads
   *  as numeric, so a balance sheet lines up without anybody formatting it. */
  numeric?: boolean
  /** Emphasised: totals and subtotals. */
  strong?: boolean
  /** Overrides where the cell's kind would put its text: figures right, words
   *  left. A column heading centred over its figures is the usual reason. */
  align?: CellAlign
  /** Background, as `#rrggbb`. Only ever a validated hex, because it is
   *  written into a stylesheet. See `readCellColour`. */
  fill?: string
  /** Ink, as `#rrggbb`, under the same rule as `fill`. */
  ink?: string
  /** A rule along the cell's top edge, in the table's colour: what a printed
   *  statement draws above a total or under its heading. Only ever on or absent. */
  ruleAbove?: boolean
  /** The same along the bottom edge. */
  ruleBelow?: boolean
  /** How many columns the cell runs across when merged, its own included.
   *  Absent for 1. The cells it runs over stay in the row, empty, so every
   *  row keeps as many cells as the header. See `sections/table/spans.ts`. */
  colspan?: number
  /** How many lines the cell runs down when merged, its own included, the
   *  header counting as the first line. Absent for 1. */
  rowspan?: number
}

/**
 * How a table trades type size against column count so it stays on the page.
 *
 * Distinct from the document-wide row padding (`layout.tableDensity`): this is
 * per table, and only about fitting width. Older documents without the field
 * read as `normal`.
 */
export type TableFit = 'normal' | 'shrink' | 'compact'

/**
 * Where a table's figures come from in CSRD, kept so they can be read again.
 *
 * Everything that names something in CSRD is a code or a name, and the numeric
 * ids are kept only as the quick way back to it: an id belongs to one reporting,
 * a code is what finds the same thing in the next. A table carrying one of these
 * is **locked**: its figures and labels are CSRD's, and changing them is done in
 * CSRD, then read again.
 */
export interface CsrdLink {
  readonly entityId: number
  readonly entityCode: string
  /** CSRD's id for the disclosure in the reporting it was read from. */
  readonly roleTypeId: number
  /** CSRD's code for the disclosure, which outlasts its id. */
  readonly roleCode: string
  /** The requirement it answers, `E1-6`, or empty. */
  readonly ref: string
  /** The disclosure's name, for showing. */
  readonly disclosure: string
  /** Which of the disclosure's tables, counting only tables. */
  readonly tableIndex: number
  /** The reporting it was read from. A table is never refreshed from another. */
  readonly reportingCode: string
  readonly reportingLabel: string
  /** The language the labels were asked for, `fr` or `en`, or empty. */
  readonly language: string
  /** When the figures now in the table were read. */
  readonly pulledAt: string
  /** CSRD's own status numbers for the disclosure when it was read. */
  readonly collected: number
  readonly controlled: number
  readonly audited: number
}

/** Tabular data, from an import or entered by hand. */
export interface TableSection extends SectionBase {
  readonly kind: 'table'
  /** The header row, if there is one. Kept apart from the body so it can
   *  repeat across a page break in print. */
  header: TableCell[]
  rows: TableCell[][]
  /** Shown under the table, for a source or a basis. */
  caption: string
  /** Rule between every row, rather than only under the header. */
  ruled: boolean
  /**
   * How tightly the table packs into the page width.
   *
   * `normal` keeps the house size; `shrink` scales the type down with the
   * column count; `compact` uses a fixed smaller size. Chosen at import for
   * wide sheets, and changeable afterwards in the inspector.
   */
  fit: TableFit
  /**
   * Optional share of the table width per column, as percentages.
   *
   * Empty / omitted means the browser (or the fit scale) decides. When set,
   * the length should match the column count; a mismatched list is ignored
   * at render time so an edited table never breaks the page.
   */
  columnWidths: number[]
  /**
   * How this table is drawn where it parts from the document's tables: its
   * head, its colour, its lines, its rows, a label column, a total row. See
   * `sections/table/look.ts`. Absent, or any setting absent, and the table
   * follows the document.
   */
  look?: TableLook
  /**
   * The style of the library this table follows, by id. Absent: the library's
   * default, if there is one. `0`: no style, the table follows the document and
   * its own look alone. The `look` above is then what this table adjusts.
   */
  styleId?: number
  /**
   * Set when the table was read from CSRD and is meant to stay in step with it.
   * Absent for a table somebody typed or imported from a file.
   */
  csrd?: CsrdLink
}

/** A picture. Held as a data URL, so a report is self-contained. */
export interface ImageSection extends SectionBase {
  readonly kind: 'image'
  source: string
  caption: string
  /** Percentage of the text width. */
  width: number
}

/**
 * One reported figure, with everything a reader needs to trust it.
 *
 * The atom of a sustainability statement. A number on its own says nothing: it
 * needs its unit, the period it belongs to, the same figure for the
 * comparative period, and how it was arrived at. Every one of those is a
 * separate field rather than something written into the value, because each is
 * read on its own: a machine reads the value, an auditor reads the method, and
 * a reader compares the two periods.
 *
 * Values are strings, not numbers, and deliberately. A figure arrives as it
 * was published, with its own separators and its own precision, and parsing it
 * into a float to display it again would round somebody's disclosure.
 */
export interface DatapointSection extends SectionBase {
  readonly kind: 'datapoint'
  /** What the figure is, in words. */
  label: string
  value: string
  /** tCO2e, MWh, %, FTE. Free text: the list of units is not closed. */
  unit: string
  /** The same figure for the comparative period, if there is one. */
  comparative: string
  /** What the two periods are called. */
  period: string
  comparativePeriod: string
  /** How it was arrived at: boundary, method, estimate, exclusions. */
  method: string
}

/**
 * A target, and how far along it is.
 *
 * ESRS asks for a base year, a target year and the progress between them, and
 * a target stated without its base is not checkable: "reduce emissions 42%"
 * against what, by when. So the three are fields and the progress is derived
 * from them rather than typed, which means it cannot disagree with them.
 *
 * The progress works for a target in either direction, because it is measured
 * as the distance travelled from the base towards the target rather than as a
 * ratio to it. Emissions falling and revenue rising are the same calculation.
 */
export interface TargetSection extends SectionBase {
  readonly kind: 'target'
  label: string
  unit: string
  baseYear: string
  baseValue: string
  targetYear: string
  targetValue: string
  /** Where it stands now. Blank until there is something to report. */
  currentValue: string
  /** The basis: absolute or intensity, scope, whether it is science based. */
  note: string
}

/** One topic on the materiality matrix. */
export interface MaterialityEntry {
  readonly id: string
  topic: string
  /** How material the impact is, 0 to 5. Outward: what the company does. */
  impact: number
  /** How material it is financially, 0 to 5. Inward: what happens to it. */
  financial: number
}

/**
 * The double materiality assessment, plotted.
 *
 * The one picture every sustainability statement carries, and the one no
 * existing kind can draw: two axes that mean different things, a threshold on
 * each, and a labelled point per topic. A bar chart of the same numbers would
 * lose the whole point, which is that a topic can be material on one axis and
 * not the other.
 *
 * Impact materiality runs outward, financial materiality inward. Naming them
 * that way round matters: it is the distinction the CSRD turns on, and a
 * matrix with the axes swapped says the opposite of what its author meant.
 */
export interface MaterialitySection extends SectionBase {
  readonly kind: 'materiality'
  entries: MaterialityEntry[]
  /** Where the line is drawn, on both axes. */
  threshold: number
  caption: string
}

/** One impact, risk or opportunity. */
export interface IroEntry {
  readonly id: string
  topic: string
  /** Which it is. The three the standards distinguish. */
  type: 'impact' | 'risk' | 'opportunity'
  /** Where in the value chain: upstream, own operations, downstream. */
  position: 'upstream' | 'own' | 'downstream'
  /** Short, medium or long term, as ESRS 1 defines them. */
  horizon: 'short' | 'medium' | 'long'
  /** What was concluded, in the author's words. */
  conclusion: string
}

/**
 * The impacts, risks and opportunities found to be material.
 *
 * A table with columns nobody would choose freely: the type, the place in the
 * value chain and the time horizon are the three things ESRS asks for about
 * every one of them, and a report that leaves a column out is one that gets
 * sent back. Fixed columns are the point.
 */
export interface IroSection extends SectionBase {
  readonly kind: 'iro'
  entries: IroEntry[]
  caption: string
}

/** One principal risk, as a management report describes it. */
export interface RiskEntry {
  readonly id: string
  /** The family it belongs to, as the report groups them: strategic, operational. */
  category: string
  title: string
  /** How severe it is judged once the controls in place are allowed for. */
  level: 'high' | 'medium' | 'low'
  /** What could happen, and what it would do. */
  description: string
  /** What is done about it. */
  management: string
}

/**
 * The principal risks and uncertainties.
 *
 * What the officers signing an annual financial report attest the management
 * report describes. See `sections/risks` for why it is not an IRO register.
 */
export interface RisksSection extends SectionBase {
  readonly kind: 'risks'
  entries: RiskEntry[]
  caption: string
}

/**
 * A greenhouse gas inventory.
 *
 * The subtotals and the totals are not fields. They are worked out from the
 * lines in `domain/ghg.ts`, which is the whole reason this is a kind rather
 * than a table with the right headings: a scope 3 whose fifteen categories do
 * not sum to the scope 3 total is caught by nobody until somebody adds them up
 * by hand.
 */
export interface GhgSection extends SectionBase {
  readonly kind: 'ghg'
  lines: GhgLine[]
  /** tCO2e, almost always, but not this module's business to insist. */
  unit: string
  period: string
  comparativePeriod: string
  /** What the intensity is per: revenue, floor area, headcount. */
  intensityLabel: string
  intensityValue: string
  intensityComparative: string
  caption: string
}

/**
 * The EU taxonomy figures.
 *
 * Exactly three ratios, because the regulation prescribes exactly three. They
 * are not rows somebody adds, which is why they are a fixed list rather than
 * an array anybody can grow.
 */
export interface TaxonomySection extends SectionBase {
  readonly kind: 'taxonomy'
  kpis: TaxonomyKpi[]
  /** The currency the amounts are in, for the column heading. */
  unit: string
  period: string
  caption: string
}

/**
 * The index of what the document discloses.
 *
 * Holds nothing of its own: it is built from the ESRS tags on the sections
 * around it whenever the document changes, exactly as the contents page is
 * built from the headings. Storing the entries would mean an index that goes
 * quietly wrong the first time somebody moves a chapter.
 */
export interface DisclosureIndexSection extends SectionBase {
  readonly kind: 'disclosureIndex'
  /** Whether each entry names the section it points at. */
  showTitles: boolean
}

/** One figure worth pulling out. */
export interface Kpi {
  readonly id: string
  label: string
  value: string
  /** Movement against the comparative, as the author wrote it. */
  change: string
  /** Which way the movement should read. `auto` leaves it uncoloured. */
  direction: 'up' | 'down' | 'auto'
}

/**
 * A chart drawn from figures typed here, flowing with the text.
 *
 * What it shows is `ChartContent` (see `chart.ts`), shared with a chart placed
 * freely on a designed page. Wiring a chart to a live source is a different
 * feature and a different section.
 */
export interface ChartSection extends SectionBase, ChartContent {
  readonly kind: 'chart'
  caption: string
  /** Height on the page, in millimetres. */
  height: number
}

/**
 * One note of the annex.
 *
 * Numbered separately from the heading structure, because annex notes carry
 * their own numbering that readers cite: "note 12" has to stay note 12 even
 * when a chapter is added before it.
 */
export interface NoteSection extends SectionBase {
  readonly kind: 'note'
  number: string
  html: string
}

/** One entity in the scope of consolidation. */
export interface ScopeEntry {
  readonly id: string
  entity: string
  country: string
  /** Full, proportionate, equity: how the entity is consolidated. */
  method: string
  /** Percentage of control, as written. */
  controlPct: string
  /** Percentage of interest, as written. */
  interestPct: string
}

/**
 * The scope of consolidation.
 *
 * A table with fixed columns rather than a free one, because these five
 * columns are what a scope note is required to show, and a scope missing its
 * method column is a scope that has to be sent back.
 */
export interface ScopeSection extends SectionBase {
  readonly kind: 'scope'
  entries: ScopeEntry[]
  caption: string
}

/** One term and what it means. */
export interface GlossaryEntry {
  readonly id: string
  term: string
  definition: string
}

/** A glossary. */
export interface GlossarySection extends SectionBase {
  readonly kind: 'glossary'
  entries: GlossaryEntry[]
}

/**
 * A rule, or a measured gap.
 *
 * Both are the same decision: separating one part of a page from the next.
 * One does it visibly and one does it with white space.
 */
export interface DividerSection extends SectionBase {
  readonly kind: 'divider'
  variant: 'rule' | 'space'
  /** Height of the gap, in millimetres. Ignored by a rule. */
  space: number
}

/** A row of headline figures. */
export interface KpiSection extends SectionBase {
  readonly kind: 'kpi'
  items: Kpi[]
}

/**
 * The contents page.
 *
 * Holds no content of its own: it is generated from the headings and groups
 * around it whenever the document changes. Storing the entries would mean a
 * contents page that silently goes stale.
 */
export interface ContentsSection extends SectionBase {
  readonly kind: 'contents'
  /** How deep to list. */
  depth: 1 | 2 | 3
  /** The named text style of the title (the document's own `textStyles`), by id; empty: the page's heading. */
  titleStyleId: string
  /** A line under the title, such as what the document is an extract of. */
  subtitle: string
  /** The page each entry is on. */
  pageNumbers: boolean
  /** The line under an entry: how many pages it runs, and what its heading says about it. */
  showDetail: boolean
  /**
   * The named text style of each level (the document's own `textStyles`), by
   * id, for levels one to three. Empty: the level is set as the page sets it.
   */
  levelStyleIds: readonly [string, string, string]
}

/** One person who signs. */
export interface Signatory {
  readonly id: string
  name: string
  role: string
  place: string
}

/** The signature block. */
export interface SignaturesSection extends SectionBase {
  readonly kind: 'signatures'
  signatories: Signatory[]
  /** Printed above the signatures. */
  statement: string
}

/** A forced page break in the printed document. */
export interface PageBreakSection extends SectionBase {
  readonly kind: 'pageBreak'
  /**
   * A picture behind the page this break opens.
   *
   * On the break rather than on a page, because a page is not a thing this
   * document has: a report is a tree of sections, and where one page ends and
   * the next begins is settled when it is printed. A page break is the one
   * place an author says "a new page starts here", which makes it the only
   * honest place to say what that page looks like.
   *
   * Empty falls through to the document's own background.
   */
  readonly background: PageBackground
  /**
   * Whether the page this break opens is a double page: two facing pages laid
   * out as one, the way a report opens a chapter across a spread.
   *
   * On the break for the same reason as the picture: it is a property of the
   * page that starts here. The page is laid out twice as wide and prints as
   * one sheet twice as wide, which is how a spread reaches a PDF reader.
   */
  readonly spread: boolean
}

/**
 * Elements placed freely on the page this section stands on.
 *
 * A section, so it has a place in the outline and travels from page to page
 * like anything else, and takes no room in the flow: what it holds is laid
 * over the page, wherever on the page its author put it. See `domain/free.ts`
 * for what an element is and how it is placed.
 */
export interface LayerSection extends SectionBase {
  readonly kind: 'layer'
  /** In drawing order: the last is on top. */
  readonly elements: readonly FreeElement[]
}

/**
 * A single picture, alone on its own page.
 *
 * Distinct from `PageBreakSection`'s background: that one is a backdrop for
 * whatever comes after it, sized and dimmed to still carry text over it. This
 * is the opposite case — a page whose only content is the picture itself, for
 * a divider or an insert finished entirely outside the document (a scanned
 * page, an exported design) that must not have anything drawn over it and
 * must not share its page with anything else.
 */
export interface ImagePageSection extends SectionBase {
  readonly kind: 'imagePage'
  /** The picture, as a data URL. Empty when there is none yet. */
  image: string
  /**
   * A two-page spread rather than a single page.
   *
   * For a plate designed across a magazine's centre pages: the picture is
   * twice as wide as one page, and prints as its left and right halves on two
   * consecutive pages rather than being scaled or cut down to one. On screen
   * it is shown at its full width, since the canvas is one continuous sheet
   * and there is no facing page to cross.
   */
  spread: boolean
}

/**
 * A page imported from a PDF, redrawn as it was printed.
 *
 * Its own page, like a full-page picture, and as exact: the background holds
 * everything that is not text, and every word is drawn back where the PDF had
 * it. What sets it apart is that the text stays text: a frame can be rewritten
 * in place, and only that frame then flows (see `domain/facsimile`).
 */
export interface FixedPageSection extends SectionBase {
  readonly kind: 'fixedPage'
  /** The page's size in points, which every position on it is measured in. */
  width: number
  height: number
  /** Everything on the page but its text, as a data URL. Empty for a page with nothing behind its text. */
  background: string
  frames: FacsimileFrame[]
  /** Which page of which file it came from, for the person tracing it back. */
  sourceFile: string
  sourcePage: number
}

/** Any section. */
export type Section =
  | CoverSection
  | GroupSection
  | HeadingSection
  | TextSection
  | TableSection
  | ImageSection
  | ChartSection
  | KpiSection
  | NoteSection
  | ScopeSection
  | GlossarySection
  | ContentsSection
  | SignaturesSection
  | DividerSection
  | PageBreakSection
  | ImagePageSection
  | FixedPageSection
  | DatapointSection
  | TargetSection
  | MaterialitySection
  | IroSection
  | RisksSection
  | GhgSection
  | TaxonomySection
  | DisclosureIndexSection
  | LayerSection

/**
 * A new identifier.
 *
 * `crypto.randomUUID` where it exists, which is everywhere this application
 * runs, with a fallback for a non-secure context since an id only has to be
 * unique within one document.
 */
export function newId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

/** Whether a section can hold others. */
export function isGroup(section: Section): section is GroupSection {
  return section.kind === 'group'
}

/**
 * A new section of a given kind.
 *
 * Delegates to the kind's own definition, which lives in `sections/<kind>/`.
 * This module describes what a section *is*; how each kind is built is the
 * kind's business, and keeping it there is what makes adding one a matter of
 * adding a folder.
 *
 * The title is content and arrives already translated. See the note in
 * `sections/index.ts`.
 */
export function createSection(kind: SectionKind, title = ''): Section {
  return createSectionOfKind(kind, newId(), title)
}

export function emptyCell(): TableCell {
  return { value: '' }
}

export function newKpi(): Kpi {
  return { id: newId(), label: '', value: '', change: '', direction: 'auto' }
}

export function newSignatory(): Signatory {
  return { id: newId(), name: '', role: '', place: '' }
}

export function newSeries(): ChartSeries {
  return { id: newId(), label: '', values: [0, 0, 0], colour: '' }
}

export function newScopeEntry(): ScopeEntry {
  return { id: newId(), entity: '', country: '', method: '', controlPct: '', interestPct: '' }
}

/** A fresh row of the materiality matrix, sitting on the threshold. */
export function newMaterialityEntry(): MaterialityEntry {
  return { id: newId(), topic: '', impact: 3, financial: 3 }
}

/** A fresh impact, risk or opportunity. */
export function newIroEntry(): IroEntry {
  return {
    id: newId(),
    topic: '',
    type: 'impact',
    position: 'own',
    horizon: 'short',
    conclusion: '',
  }
}

/** A fresh principal risk, judged moderate until somebody says otherwise. */
export function newRiskEntry(): RiskEntry {
  return {
    id: newId(),
    category: '',
    title: '',
    level: 'medium',
    description: '',
    management: '',
  }
}

export function newGlossaryEntry(): GlossaryEntry {
  return { id: newId(), term: '', definition: '' }
}

/**
 * A deep copy of a section, with fresh ids throughout.
 *
 * Fresh ids matter: duplicating a group and keeping its children's ids would
 * produce two sections that comments, selection and drag operations cannot
 * tell apart.
 */
export function duplicateSection(section: Section): Section {
  const copy = structuredClone(section)
  return withNewIds(copy)
}

/**
 * Fresh ids throughout a copied section.
 *
 * Walks the value generically rather than knowing which kinds carry what: a
 * chart's series, a scope's entities, a group's children and a signature
 * block's signatories are all just arrays of objects with an `id`, and
 * treating them the same way means a new kind with its own list needs no
 * change here at all.
 *
 * That matters more than it looks. The version of this that listed kinds by
 * name was the last place outside `sections/` that had to be edited to add
 * one, and the failure it produced was silent: a duplicated chart sharing its
 * series ids with the original, so editing one edited both.
 */
function withNewIds(section: Section): Section {
  return refreshIds(section) as Section
}

/** Anything with an `id` this may replace. */
function hasId(value: unknown): value is { id: string } {
  return (
    typeof value === 'object' && value !== null && 'id' in value && typeof value.id === 'string'
  )
}

function refreshIds(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(refreshIds)

  if (typeof value !== 'object' || value === null) return value

  const copy: Record<string, unknown> = {}
  for (const [key, held] of Object.entries(value)) {
    copy[key] = refreshIds(held)
  }

  if (hasId(value)) copy['id'] = newId()
  return copy
}
